import { useRef, useState } from 'react';
import { AlertTriangle, FileText, Info, Loader2, Trash2, Upload } from 'lucide-react';
import { Badge, Button, Switch, cn } from '@respectly/ui';
import type { ChatBrandDocumentDto } from '@respectly/shared-types';
import { useChatWidgetsStore } from '../../stores/useChatWidgetsStore';

/**
 * The widget's own documents.
 *
 * The picker above chooses from Respectly's curated corpus, which is baked into
 * the retrieval service's container image. That works for widgets on our own
 * sites and not at all for a widget embedded somewhere else, which has to answer
 * about someone else's product. These are that widget's own material.
 *
 * Uploads commit immediately rather than joining the page's draft-then-save flow.
 * A file listed here has definitely been stored; there is no state in which the
 * list shows a document the assistant cannot yet read.
 */
export function BrandDocuments({ brandId }: { brandId: string }) {
  const documents = useChatWidgetsStore((s) => s.documents);
  const loading = useChatWidgetsStore((s) => s.loadingDocuments);
  const uploading = useChatWidgetsStore((s) => s.uploading);
  const uploadDocuments = useChatWidgetsStore((s) => s.uploadDocuments);
  const setDocumentActive = useChatWidgetsStore((s) => s.setDocumentActive);
  const removeDocument = useChatWidgetsStore((s) => s.removeDocument);
  const brandCorpusConfigured = useChatWidgetsStore((s) => s.brandCorpusConfigured);

  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<Array<{ filename: string; message: string }>>([]);
  const input = useRef<HTMLInputElement>(null);

  const send = async (files: FileList | File[] | null) => {
    const list = Array.from(files ?? []);
    if (!list.length) return;
    const result = await uploadDocuments(brandId, list);
    // Per-file outcomes: one file over the size limit must not read as though
    // the whole drop failed.
    setRejected(result?.rejected ?? []);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            This widget&rsquo;s own documents
          </h3>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
            Markdown or plain text. Use these for a site that isn&rsquo;t Respectly&rsquo;s — they
            are searched alongside anything ticked above.
          </p>
        </div>
        {documents.length > 0 && (
          <span className="shrink-0 text-sm text-slate-500 dark:text-slate-400">
            {documents.length} file{documents.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {brandCorpusConfigured === false && documents.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            These documents are saved but are <strong>not being searched</strong> — the retrieval
            service cannot reach them, so this widget is answering from the curated Respectly
            documentation only. Check <span className="font-mono">CHAT_GATEWAY_BASE_URL</span> on
            the ops-tools container app.
          </p>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void send(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-xl border-2 border-dashed p-6 text-center transition-colors',
          dragging
            ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30'
            : 'border-slate-200 dark:border-slate-700'
        )}
      >
        <Upload className="mx-auto h-6 w-6 text-slate-400" />
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
          Drop <span className="font-mono text-xs">.md</span> files here, or{' '}
          <button
            type="button"
            className="font-medium text-teal-700 underline underline-offset-2 dark:text-teal-400"
            onClick={() => input.current?.click()}
          >
            choose files
          </button>
          .
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          As many documents as you need, 256 KB each. Drop a whole folder.
        </p>
        <input
          ref={input}
          type="file"
          multiple
          accept=".md,.markdown,.txt"
          className="hidden"
          onChange={(e) => {
            void send(e.target.files);
            e.target.value = '';
          }}
        />
        {uploading && (
          <p className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Loader2 className="h-4 w-4 animate-spin" /> Uploading&hellip;
          </p>
        )}
      </div>

      {rejected.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/50 dark:bg-amber-950/30">
          <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
            {rejected.length} file{rejected.length === 1 ? '' : 's'} not saved
          </p>
          <ul className="mt-1 space-y-0.5">
            {rejected.map((r) => (
              <li key={r.filename} className="text-xs text-amber-800 dark:text-amber-300">
                <span className="font-mono">{r.filename}</span> — {r.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading documents&hellip;</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No documents uploaded. This widget answers only from what is ticked above.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
          {documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onToggle={(next) => void setDocumentActive(brandId, doc.id, next)}
              onRemove={() => void removeDocument(brandId, doc.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DocumentRow({
  doc,
  onToggle,
  onRemove,
}: {
  doc: ChatBrandDocumentDto;
  onToggle: (next: boolean) => void;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="flex items-start gap-3 p-3">
      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {doc.title}
          </span>
          <Badge variant="secondary" className="font-mono text-[11px]">
            {doc.slug}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {Math.max(1, Math.round(doc.sizeBytes / 1024))} KB · {doc.sectionCount} section
          {doc.sectionCount === 1 ? '' : 's'} · {doc.sourceFilename}
        </p>
        {/* A single section means no `##` headings, so the whole file retrieves as
            one block. It still works — it just answers less precisely, and that is
            worth saying now rather than leaving to be discovered. */}
        {doc.sectionCount <= 1 && (
          <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            No <span className="font-mono">##</span> headings, so this is searched as one block.
            Splitting it into sections gives more precise answers.
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Switch
          checked={doc.isActive}
          onCheckedChange={onToggle}
          aria-label={`${doc.isActive ? 'Stop' : 'Start'} answering from ${doc.title}`}
        />
        {confirming ? (
          <div className="flex items-center gap-1">
            <Button type="button" size="sm" variant="destructive" onClick={onRemove}>
              Remove
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setConfirming(true)}
            aria-label={`Remove ${doc.title}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  );
}
